import { create } from "zustand";
import type { Camp } from "@/hooks/useFieldDebate";

/**
 * Field Debate generation progress store.
 *
 * Tracks an in-flight 4-pass pipeline run end-to-end. Survives component
 * unmount (Zustand singleton), so navigating away from Analyze and back
 * keeps the progress visible. The actual debate_map result lands in the
 * React Query cache (key `["field-debate", conceptId]`) — this store
 * only tracks running-state and progressive partial reveals.
 */

export type FieldDebateStep =
  | 0 // idle / preparing
  | 1 // clustering (skipped if already clustered)
  | 2 // Pass 1: define camps
  | 3 // Pass 2: assign papers
  | 4 // Pass 3: claim mapping (skipped if no claims)
  | 5; // Pass 4: per-camp blind spots

interface FieldDebateGenerationState {
  conceptId: string | null;
  jobId: string | null;
  isPending: boolean;
  /** True for ~5 seconds after a successful finish — used by OperationBar
   * to auto-fade the success pill. Reset on next start or explicit reset. */
  isCompleted: boolean;
  error: string | null;

  /** Current step number (0-5). 0 = preparing, 5 = Pass 4 in flight. */
  step: FieldDebateStep;
  /** Human-readable label for the current step. */
  stepLabel: string;
  /** Total steps the backend will report (always 5). */
  totalSteps: number;
  /** Pass 4 sub-progress: per-camp blind spots completed. */
  pass4Completed: number;
  pass4Total: number;
  /** When the run started. Used to compute elapsed time in the UI. */
  startedAt: number | null;
  /** Camps emitted by the backend after Pass 1 / Pass 2 — render these as a
   * skeleton while Pass 3/4 finish. Cleared on terminal events. */
  partialCamps: Camp[] | null;
  /** Abort controller for the in-flight start() pipeline (probe → POST →
   * SSE). Stored here so OperationBar's pill-cancel can abort the
   * preflight phase too — without it, cancel during the active-probe /
   * cancel-if-needed / POST window before jobId arrives is a no-op. */
  cancelController: AbortController | null;
  /** FD Stage 3: a batch REFRESH was submitted for this concept and hasn't
   * landed yet. Unlike a pending full build the map stays complete
   * (non-partial), so the partial-map poll never runs — this flag drives the
   * passive "Refreshing…" chip + 20s map poll instead. Deliberately NOT part
   * of `initialState`, so run-lifecycle `reset()` calls (batch_submitted,
   * build_pending, cancel) don't clobber it; cleared explicitly on `fail`,
   * on a new `start` (a rebuild supersedes the pending refresh), or by the
   * status bar once `refresh_meta.refreshed_at` advances past `since`. */
  pendingRefresh: { conceptId: string; since: number } | null;

  start: (conceptId: string, jobId: string) => void;
  setStep: (step: FieldDebateStep, label: string) => void;
  setPartialCamps: (camps: Camp[]) => void;
  setPass4Progress: (completed: number, total: number) => void;
  setCancelController: (controller: AbortController | null) => void;
  setPendingRefresh: (
    pending: { conceptId: string; since: number } | null,
  ) => void;
  fail: (error: string) => void;
  /** Mark generation complete and clear running-state. The actual result
   * is reloaded by invalidating the React Query cache (caller's job). */
  finish: () => void;
  reset: () => void;
}

const initialState = {
  conceptId: null,
  jobId: null,
  isPending: false,
  isCompleted: false,
  error: null,
  step: 0 as FieldDebateStep,
  stepLabel: "",
  totalSteps: 5,
  pass4Completed: 0,
  pass4Total: 0,
  startedAt: null,
  partialCamps: null,
  cancelController: null,
};

export const useFieldDebateGenerationStore = create<FieldDebateGenerationState>(
  (set) => ({
    ...initialState,
    pendingRefresh: null,

    start: (conceptId, jobId) =>
      set({
        ...initialState,
        // A new run supersedes any pending refresh — a completed rebuild's
        // map carries no newer refresh_meta, so a stale flag would poll and
        // show the chip forever.
        pendingRefresh: null,
        conceptId,
        jobId,
        isPending: true,
        startedAt: Date.now(),
        stepLabel: "Preparing…",
      }),

    setStep: (step, label) => set({ step, stepLabel: label }),

    setPartialCamps: (camps) => set({ partialCamps: camps }),

    setPass4Progress: (completed, total) =>
      set({ pass4Completed: completed, pass4Total: total }),

    setCancelController: (cancelController) => set({ cancelController }),

    setPendingRefresh: (pendingRefresh) => set({ pendingRefresh }),

    fail: (error) =>
      set({
        isPending: false,
        error,
        step: 0,
        stepLabel: "",
        partialCamps: null,
        pendingRefresh: null,
      }),

    finish: () =>
      set({
        isPending: false,
        isCompleted: true,
        step: 5,
        stepLabel: "Complete",
        partialCamps: null,
      }),

    reset: () => set({ ...initialState }),
  }),
);
